import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'

import { loginRequest } from '@/lib/auth/msal'
import { queryClient } from '@/lib/query/query-client'

import { authApi } from '../api/auth.api'
import { useAuthStore } from '../store/auth.store'

/** Navigates away to Microsoft's sign-in page; the app re-mounts on return. */
export function useSignIn() {
  const { instance } = useMsal()
  return () => void instance.loginRedirect(loginRequest)
}

export function useLogout() {
  const signOut = useAuthStore((state) => state.signOut)

  return useMutation({
    mutationFn: async () => {
      // Nothing cached under the previous identity may leak into the next one.
      queryClient.clear()
      signOut()
    },
  })
}

/**
 * Runs once, near the app root — only ever mounted inside an `MsalProvider`.
 * Once MSAL has finished any startup/redirect work, this pushes whether an
 * Entra account exists into the store, and — if one does — fetches this
 * person's TQ Capacity role from the backend. `isHydrated` flips either way, so
 * route guards know when it's safe to make a decision.
 */
export function useSyncAuthProfile() {
  const { accounts, inProgress, instance } = useMsal()
  const isHydrated = useAuthStore((state) => state.isHydrated)
  const setAccount = useAuthStore((state) => state.setAccount)
  const setUser = useAuthStore((state) => state.setUser)
  const setAccessDenied = useAuthStore((state) => state.setAccessDenied)
  const setHydrated = useAuthStore((state) => state.setHydrated)

  useEffect(() => {
    if (isHydrated || inProgress !== InteractionStatus.None) return

    const account = accounts[0]
    setAccount(Boolean(account))

    if (!account) {
      setHydrated()
      return
    }

    // acquireTokenSilent (in lib/api/auth-token.ts) needs an active account
    // to know which one to get a token for.
    instance.setActiveAccount(account)

    let cancelled = false
    authApi
      .me()
      .then((user) => {
        if (!cancelled) setUser(user)
      })
      // 403 means the portal never invited this tenant account. Every other
      // failure is blocked the same way: without a profile there are no roles,
      // and a shell with no roles is a half-working app, not a usable one.
      .catch(() => {
        if (!cancelled) setAccessDenied()
      })
      .finally(() => {
        if (!cancelled) setHydrated()
      })

    return () => {
      cancelled = true
    }
  }, [accounts, inProgress, instance, isHydrated, setAccessDenied, setAccount, setHydrated, setUser])
}
